import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Camera, ImagePlus, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { canvasToJpeg, fileToResizedJpeg } from "@/lib/image";

export function CameraCapture({ photo, onPhoto }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const [live, setLive] = useState(false);

  const stop = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setLive(false);
  };

  useEffect(() => stop, []);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Camera not available here — upload a photo instead");
      return fileRef.current?.click();
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
      streamRef.current = stream;
      setLive(true);
      requestAnimationFrame(() => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); } });
    } catch {
      toast.error("Could not open the camera — upload a photo instead");
      fileRef.current?.click();
    }
  };

  const snap = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return toast.error("Camera is still starting…");
    onPhoto(canvasToJpeg(v, v.videoWidth, v.videoHeight));
    stop();
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) return toast.error("Please use a JPEG, PNG or WEBP photo");
    try { onPhoto(await fileToResizedJpeg(f)); } catch (err) { toast.error(err.message); }
  };

  return (
    <div data-testid="camera-capture">
      <input ref={fileRef} data-testid="photo-file-input" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden" onChange={onFile} />

      {live ? (
        <div className="relative rounded-2xl overflow-hidden bg-black aspect-[4/3] max-h-[420px]">
          <video ref={videoRef} data-testid="camera-video" playsInline muted className="w-full h-full object-cover" />
          <div className="absolute bottom-4 inset-x-0 flex justify-center gap-3">
            <Button data-testid="camera-snap-btn" onClick={snap} className="h-12 px-6 rounded-full bg-[#D97706] hover:bg-[#78350F] text-white font-semibold">
              <Camera className="h-5 w-5 mr-2" /> Take photo
            </Button>
            <Button data-testid="camera-cancel-btn" onClick={stop} variant="secondary" className="h-12 rounded-full"><X className="h-5 w-5" /></Button>
          </div>
        </div>
      ) : photo ? (
        <div className="relative rounded-2xl overflow-hidden border border-stone-200 bg-stone-100">
          <img src={photo.preview} alt="Soil photo" data-testid="photo-preview" className="w-full max-h-[420px] object-contain" />
          <div className="absolute top-3 right-3 flex gap-2">
            <Button data-testid="photo-retake-btn" size="sm" onClick={start} className="rounded-full bg-white/90 text-emerald-950 hover:bg-white"><RefreshCw className="h-4 w-4 mr-1" /> Retake</Button>
            <Button data-testid="photo-clear-btn" size="sm" onClick={() => onPhoto(null)} className="rounded-full bg-white/90 text-red-600 hover:bg-white"><X className="h-4 w-4" /></Button>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border-2 border-dashed border-stone-300 bg-[#FAF9F6] p-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button data-testid="open-camera-btn" onClick={start} className="h-14 px-7 rounded-2xl bg-[#2D6A4F] hover:bg-[#143D29] text-white text-base font-semibold">
            <Camera className="h-6 w-6 mr-2" /> Open camera
          </Button>
          <span className="text-stone-400 text-sm">or</span>
          <Button data-testid="upload-photo-btn" onClick={() => fileRef.current?.click()} variant="outline" className="h-14 px-7 rounded-2xl border-stone-300 text-base font-semibold">
            <ImagePlus className="h-6 w-6 mr-2" /> Upload photo
          </Button>
        </div>
      )}
    </div>
  );
}
